import { DropDown, Option } from "../../components/Select/DropDown";
import {
  fetchCustomDataTypeById,
  fetchCustomDataTypesByApplication,
  fetchDataTypes,
} from "../../services/state/custom-data-types/customDataTypes-async-calls";
import { AppDispatch, RootState } from "@/services/state/store";
import { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useParams } from "react-router-dom";
import {
  Center,
  Checkbox,
  HStack,
  Input,
  Text,
  useDisclosure,
  useToast,
} from "@chakra-ui/react";
import { setSelectedCustomDataTypeAttributes } from "../../services/state/custom-data-types/customDataTypes";
import { cdtAttributesInterface } from "../../services/state/custom-data-types/customDataTypes-interface";
import { getAxiosClient } from "../../services/HttpsService";
import { ChevronDownIcon, ChevronUpIcon, DeleteIcon } from "@chakra-ui/icons";
import { v4 } from "uuid";

export const useCustomDataType = () => {
  const dispatch = useDispatch<AppDispatch>();
  const toast = useToast();
  const { cdtId } = useParams();

  const selectedCustomDataType = useSelector(
    (state: RootState) => state.customDataTypes.selectedCustomDataType
  );
  const selectedCustomDataTypeAttrs = useSelector(
    (state: RootState) => state.customDataTypes.selectedCustomDataTypeAttr
  );

  const dataTypes = useSelector(
    (state: RootState) => state.customDataTypes.dataTypes
  );
  const customDataTypes = useSelector(
    (state: RootState) => state.customDataTypes.customDataTypes
  );

  const { isOpen: isEditing, onToggle: onEditToggle } = useDisclosure();

  useEffect(() => {
    if (cdtId) {
      dispatch(fetchCustomDataTypeById(cdtId));
    }
    dispatch(fetchDataTypes());
  }, []);

  useEffect(() => {
    if (selectedCustomDataType) {
      dispatch(
        fetchCustomDataTypesByApplication(selectedCustomDataType.appUuid)
      );
    }
  }, [selectedCustomDataType]);

  const columns = [
    {
      Header: "Name",
      accessor: "name",
    },
    {
      Header: "Type",
      accessor: "type",
    },
    {
      Header: "Mandatory",
      accessor: "isMandatory",
    },
    {
      Header: "Mutiple",
      accessor: "isMultiple",
    },
    {
      Header: "",
      accessor: "actions",
    },
  ];

  const dataTypeOptions = dataTypes.map((type) => ({
    id: type.dataTypCd,
    name: type.dataTypNm,
    component: <Text>{type.dataTypNm}</Text>,
  }));

  const customDataTypeOptions = customDataTypes.map((type) => ({
    id: type.uuid,
    name: type.dataTypeName,
    component: <Text>{type.dataTypeName}</Text>,
  }));

  const moveAttribute = (originalIndex: number, direction: "up" | "down") => {
    const attributes = [...selectedCustomDataTypeAttrs];
    const targetIndex =
      direction === "up" ? originalIndex - 1 : originalIndex + 1;
    if (targetIndex < 0 || targetIndex >= attributes.length) return;

    [attributes[originalIndex], attributes[targetIndex]] = [
      attributes[targetIndex],
      attributes[originalIndex],
    ];

    dispatch(setSelectedCustomDataTypeAttributes(attributes));
  };

  // Helper function to update attribute by UUID
  const updateAttributeByUuid = (
    uuid: string | null,
    updates: Partial<cdtAttributesInterface>
  ) => {
    if (!uuid) return;
    const attributes = [...selectedCustomDataTypeAttrs];
    const attrIndex = attributes.findIndex((attr) => attr.uuid === uuid);
    if (attrIndex !== -1) {
      attributes[attrIndex] = { ...attributes[attrIndex], ...updates };
      dispatch(setSelectedCustomDataTypeAttributes(attributes));
    }
  };

  const selectedCustomDataTypeAttributes = selectedCustomDataTypeAttrs
    ? selectedCustomDataTypeAttrs
        .filter((attr) => attr.isActive === "Y")
        .map((attr, filteredIndex) => {
          // Find the original index in the full array for move operations
          const originalIndex = selectedCustomDataTypeAttrs.findIndex(
            (a) => a.uuid === attr.uuid
          );

          return {
            name: (
              <Input
                value={attr.name}
                onChange={(e) => {
                  if (attr.uuid) {
                    updateAttributeByUuid(attr.uuid, { name: e.target.value });
                  }
                }}
                focusBorderColor="brand.500"
                isReadOnly={!isEditing}
              />
            ),
            type: (
              <HStack>
                <DropDown
                  selected={dataTypeOptions.find(
                    (type) => type.id === attr.type
                  )}
                  title={<Text></Text>}
                  options={dataTypeOptions}
                  onOptionChange={(selectedOption: Option | Option[]) => {
                    if (!Array.isArray(selectedOption) && attr.uuid) {
                      updateAttributeByUuid(attr.uuid, {
                        type: selectedOption.id,
                      });
                    }
                  }}
                  disabled={!isEditing}
                />
                {attr.type === "ANY" && (
                  <DropDown
                    selected={customDataTypeOptions.find(
                      (option) => option.id === attr.cdtUuid
                    )}
                    title={<Text></Text>}
                    options={customDataTypeOptions}
                    onOptionChange={(selectedOption: Option | Option[]) => {
                      if (!Array.isArray(selectedOption) && attr.uuid) {
                        updateAttributeByUuid(attr.uuid, {
                          cdtUuid: selectedOption.id,
                        });
                      }
                    }}
                    disabled={!isEditing}
                  />
                )}
              </HStack>
            ),
            isMandatory: (
              <Center>
                <Checkbox
                  colorScheme="brand"
                  isChecked={attr.isMandatory}
                  onChange={(e) => {
                    if (attr.uuid) {
                      updateAttributeByUuid(attr.uuid, {
                        isMandatory: e.target.checked,
                      });
                    }
                  }}
                />
              </Center>
            ),
            isMultiple: (
              <Center>
                <Checkbox
                  colorScheme="brand"
                  isChecked={attr.isMultiple}
                  onChange={(e) => {
                    if (attr.uuid) {
                      updateAttributeByUuid(attr.uuid, {
                        isMultiple: e.target.checked,
                      });
                    }
                  }}
                />
              </Center>
            ),
            actions: isEditing ? (
              <Center>
                <ChevronUpIcon
                  color="blue.500"
                  cursor="pointer"
                  onClick={() => moveAttribute(originalIndex, "up")}
                  mr={2}
                />
                <ChevronDownIcon
                  color="blue.500"
                  cursor="pointer"
                  onClick={() => moveAttribute(originalIndex, "down")}
                  mr={2}
                />
                <DeleteIcon
                  color="red.500"
                  cursor="pointer"
                  onClick={() => {
                    if (attr.uuid) {
                      updateAttributeByUuid(attr.uuid, { isActive: "N" });
                    }
                  }}
                />
              </Center>
            ) : null,
          };
        })
    : [];

  const columnData = [...selectedCustomDataTypeAttributes];

  const onAddField = () => {
    const attributes = [
      ...selectedCustomDataTypeAttrs,
      {
        uuid: v4(),
        name: "",
        type: "STR",
        cdtUuid: null,
        isMandatory: true,
        isMultiple: false,
        isActive: "Y",
      },
    ];
    dispatch(setSelectedCustomDataTypeAttributes(attributes));
  };

  const areArraysEqual = (
    arr1: cdtAttributesInterface[],
    arr2: cdtAttributesInterface[]
  ): boolean => {
    if (arr1.length !== arr2.length) return false;

    return arr1.every((item1, index) => {
      const item2 = arr2[index];
      return (
        item1.uuid === item2.uuid &&
        item1.name === item2.name &&
        item1.type === item2.type &&
        item1.cdtUuid === item2.cdtUuid &&
        item1.isMandatory === item2.isMandatory &&
        item1.isMultiple === item2.isMultiple &&
        item1.isActive === item2.isActive
      );
    });
  };

  const isEdited = selectedCustomDataType?.cdtAttributes
    ? !areArraysEqual(
        selectedCustomDataType?.cdtAttributes,
        selectedCustomDataTypeAttrs
      )
    : false;

  const onSave = async () => {
    let hasError = false;

    selectedCustomDataTypeAttrs
      .filter((attr) => attr.isActive === "Y")
      .forEach((attr) => {
        if (attr.name === "") {
          toast({
            title: "Error",
            description: "Attribute name cannot be empty.",
            status: "error",
            position: "top",
            duration: 3000,
            isClosable: true,
          });
          hasError = true;
        }
      });

    if (hasError) return;

    const data = {
      cdtUuid: selectedCustomDataType?.uuid,
      attributes: selectedCustomDataTypeAttrs.map((attr) => ({
        uuid: attr.uuid,
        attributeName: attr.name,
        attributeType: attr.type,
        cdtUuid: selectedCustomDataType?.uuid,
        isMandatory: attr.isMandatory,
        isMultiple: attr.isMultiple,
        nestedDataTypeUuid: attr.cdtUuid,
        isActive: attr.isActive,
      })),
    };

    const response = await getAxiosClient().post(
      "/api/custom-data-type-attributes/update-cdt-attributes",
      data
    );

    if (cdtId) {
      dispatch(fetchCustomDataTypeById(cdtId));
    }

    if (isEditing) onEditToggle();
  };

  return {
    selectedCustomDataType,
    columns,
    columnData,
    isEditing,
    isEdited,
    onEditToggle,
    onAddField,
    onSave,
  };
};
